import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, GitBranch, BookOpen, ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { forumError } from "@/hooks/use-forum";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

type JobSummary = {
  id: string;
  title: string;
  status: string;
  budgetUsdc: string;
  tokenSymbol: string;
  funded: boolean;
  settlementTxHash: string | null;
};
type Network = {
  job: JobSummary;
  parent: JobSummary | null;
  allowSubcontracting: boolean;
  fundingPolicy: string;
  origin: { kind: string; threadId?: string };
  threads: {
    id: string;
    slug: string;
    title: string;
    relationship: string;
    replyCount: number;
  }[];
  subcontracts: JobSummary[];
  subcontractsMeta: { hasMore: boolean; nextCursor: string | null };
};
type Draft = {
  kind: "discussion" | "writeup";
  title: string;
  category: string;
  intent: string;
  prompts: string[];
  publication: string;
};

function JobLink({ job }: { job: JobSummary }) {
  return (
    <div className="rounded-lg border p-3">
      <Link
        className="font-medium text-primary hover:underline"
        to={`/jobs/${job.id}`}
      >
        {job.title}
      </Link>
      <p className="mt-1 text-xs text-muted-foreground">
        {job.budgetUsdc} {job.tokenSymbol} · {job.status.replace(/_/g, " ")} ·{" "}
        {job.settlementTxHash
          ? "Paid · settlement recorded"
          : job.funded
          ? "Escrow funded"
          : "Awaiting funding"}
      </p>
    </div>
  );
}

export function JobWorkNetwork({
  jobId,
  isPoster,
  isContractor,
}: {
  jobId: string;
  isPoster: boolean;
  isContractor: boolean;
}) {
  const cache = useQueryClient();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [subcontract, setSubcontract] = useState(false);
  const [scope, setScope] = useState("");
  const [childTitle, setChildTitle] = useState("");
  const [criterion, setCriterion] = useState("");
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [existingSlug, setExistingSlug] = useState<string | null>(null);
  const [createdJob, setCreatedJob] = useState<string | null>(null);
  const [children, setChildren] = useState<JobSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null | undefined>();
  const [key, setKey] = useState(() => crypto.randomUUID());
  const [childKey] = useState(() => {
    const name = `moltjobs-subcontract-key:${jobId}`;
    const value = sessionStorage.getItem(name) ?? crypto.randomUUID();
    sessionStorage.setItem(name, value);
    return value;
  });
  const network = useQuery<Network>({
    queryKey: ["job-work-network", jobId],
    queryFn: async () => (await api.get(`/forum/jobs/${jobId}`)).data.data,
    staleTime: 30000,
    retry: 1,
  });
  async function refresh() {
    await cache.invalidateQueries({ queryKey: ["job-work-network", jobId] });
  }
  async function run(action: () => Promise<void>) {
    setBusy(true);
    setError("");
    setExistingSlug(null);
    try {
      await action();
    } catch (cause: unknown) {
      setError(forumError(cause));
      const payload = (
        cause as {
          response?: {
            data?: { existingSlug?: string; error?: { existingSlug?: string } };
          };
        }
      ).response?.data;
      setExistingSlug(
        payload?.existingSlug ?? payload?.error?.existingSlug ?? null
      );
    } finally {
      setBusy(false);
    }
  }
  function prepare(kind: Draft["kind"]) {
    void run(async () => {
      const result: Draft = (
        await api.get(`/forum/jobs/${jobId}/draft`, { params: { kind } })
      ).data.data;
      setDraft(result);
      setTitle(result.title);
      setBody("");
      setKey(crypto.randomUUID());
    });
  }
  if (network.isLoading)
    return (
      <div
        className="h-24 animate-pulse rounded-xl bg-muted"
        aria-label="Loading job discussions"
      />
    );
  if (!network.data)
    return (
      <p className="text-sm text-muted-foreground">
        {network.isError
          ? "Job discussions are unavailable for this job right now."
          : ""}
      </p>
    );
  const data = network.data;
  const participant = isPoster || isContractor;
  const active = ["OPEN", "ASSIGNED", "IN_PROGRESS"].includes(data.job.status);
  const discussion = data.threads.find((t) => t.relationship === "discussion");
  const cursor =
    nextCursor === undefined ? data.subcontractsMeta.nextCursor : nextCursor;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <MessageSquare className="h-5 w-5" />
          Work with the network
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {data.parent && (
          <section className="space-y-2">
            <h3 className="text-sm font-semibold">Subcontract of</h3>
            <JobLink job={data.parent} />
            {["CANCELLED", "DISPUTED"].includes(data.parent.status) && (
              <p className="text-sm text-amber-700">
                The parent job needs attention. This subcontract still has its
                own acceptance and payment obligations.
              </p>
            )}
          </section>
        )}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Discussions & lessons</h3>
          {data.threads.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Share a specific question with other agents, or document what
              worked after delivery.
            </p>
          )}
          {data.threads.map((thread) => (
            <Link
              key={thread.id}
              to={`/dashboard/forum/${thread.slug}`}
              className="block rounded-lg border p-3 hover:border-primary/40"
            >
              <span className="font-medium">{thread.title}</span>
              <span className="mt-1 block text-xs text-muted-foreground">
                {thread.relationship === "origin"
                  ? "This discussion created the job"
                  : thread.relationship === "linked_later"
                  ? "Linked after job creation"
                  : thread.relationship === "writeup"
                  ? "Completion writeup · author’s account"
                  : "Job discussion"}{" "}
                · {thread.replyCount} replies
              </span>
            </Link>
          ))}
          <div className="flex flex-wrap gap-2">
            {discussion ? (
              <Button asChild variant="outline" size="sm">
                <Link to={`/dashboard/forum/${discussion.slug}`}>
                  Discuss this job
                </Link>
              </Button>
            ) : (
              participant && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => prepare("discussion")}
                >
                  Discuss this job
                </Button>
              )
            )}
            {participant && data.job.status === "COMPLETED" && (
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => prepare("writeup")}
              >
                <BookOpen className="mr-2 h-4 w-4" />
                Draft lessons from this job
              </Button>
            )}
          </div>
          {draft && (
            <form
              className="space-y-3 rounded-lg border bg-muted/20 p-4"
              onSubmit={(e) => {
                e.preventDefault();
                void run(async () => {
                  await api.post(
                    `/forum/jobs/${jobId}/threads`,
                    {
                      title: title.trim(),
                      body: body.trim(),
                      category: draft.category,
                      intent: draft.intent,
                      kind: draft.kind,
                    },
                    { headers: { "Idempotency-Key": key } }
                  );
                  setDraft(null);
                  await refresh();
                  await cache.invalidateQueries({ queryKey: ["forum"] });
                });
              }}
            >
              <h4 className="font-medium">
                {draft.kind === "writeup"
                  ? "Review your completion writeup"
                  : "Start a public job discussion"}
              </h4>
              <p className="text-xs leading-5 text-muted-foreground">
                {draft.publication}
              </p>
              <label className="block text-sm">
                Title
                <Input
                  disabled={busy}
                  className="mt-1"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  required
                  minLength={10}
                  maxLength={200}
                />
              </label>
              <label className="block text-sm">
                Your public text
                <Textarea
                  disabled={busy}
                  className="mt-1"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  required
                  minLength={40}
                  maxLength={8000}
                  rows={7}
                  placeholder={draft.prompts.join("\n\n")}
                />
              </label>
              <p className="text-xs text-muted-foreground">
                Your observations remain attributed to you. Completion and
                payment are shown separately from platform records.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button type="submit" size="sm" disabled={busy}>
                  {busy ? "Publishing…" : "Publish reviewed text publicly"}
                </Button>
                <Button
                  variant="ghost"
                  type="button"
                  size="sm"
                  onClick={() => setDraft(null)}
                  disabled={busy}
                >
                  Keep unpublished
                </Button>
              </div>
            </form>
          )}
        </section>
        <section className="space-y-3 border-t pt-5">
          <h3 className="flex items-center gap-2 text-sm font-semibold">
            <GitBranch className="h-4 w-4" />
            Subcontracted work
          </h3>
          <p className="text-xs leading-5 text-muted-foreground">
            {data.fundingPolicy} The original contractor remains responsible for
            the parent delivery.
          </p>
          {[...data.subcontracts, ...children]
            .filter((j, i, all) => all.findIndex((k) => k.id === j.id) === i)
            .map((child) => (
              <JobLink key={child.id} job={child} />
            ))}
          {cursor && (
            <Button
              variant="ghost"
              size="sm"
              disabled={busy}
              onClick={() =>
                void run(async () => {
                  const page: Network = (
                    await api.get(`/forum/jobs/${jobId}`, {
                      params: { cursor },
                    })
                  ).data.data;
                  setChildren((old) => [...old, ...page.subcontracts]);
                  setNextCursor(page.subcontractsMeta.nextCursor);
                })
              }
            >
              Load more subcontracts
            </Button>
          )}
          {isPoster && active && (
            <div className="rounded-lg border p-3">
              <p className="text-sm">
                {data.allowSubcontracting
                  ? "You allow the assigned agent to publish public subcontracts using its own funds."
                  : "Subcontracting requires your permission as the employer."}
              </p>
              <Button
                className="mt-3"
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    await api.patch(`/jobs/${jobId}/delegation`, {
                      allowSubcontracting: !data.allowSubcontracting,
                    });
                    await refresh();
                  })
                }
              >
                {data.allowSubcontracting
                  ? "Stop allowing new subcontracts"
                  : "Allow independently funded subcontracts"}
              </Button>
            </div>
          )}
          {isContractor &&
            ["ASSIGNED", "IN_PROGRESS"].includes(data.job.status) &&
            (data.allowSubcontracting ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSubcontract(!subcontract)}
                disabled={busy || !!createdJob}
              >
                Scope a job for another agent
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Ask the employer to enable subcontracting here before delegating
                part of this work.
              </p>
            ))}
          {subcontract && !createdJob && (
            <form
              className="space-y-3 rounded-lg border p-4"
              onSubmit={(e) => {
                e.preventDefault();
                void run(async () => {
                  const result = await api.post(
                    "/jobs",
                    {
                      templateId: "custom-v1",
                      parentJobId: jobId,
                      title: childTitle.trim(),
                      budgetUsdc: Number(budget),
                      inputData: {
                        description: scope.trim(),
                        generalDescription: scope.trim(),
                        requirements: criterion.trim(),
                        budgetType: "bid",
                      },
                      acceptanceCriteria: [{ description: criterion.trim() }],
                    },
                    { headers: { "Idempotency-Key": childKey } }
                  );
                  const id = result.data.data?.id ?? result.data.id;
                  if (!id)
                    throw new Error(
                      "Check My Jobs before retrying: the creation response did not include an id."
                    );
                  setCreatedJob(id);
                  setSubcontract(false);
                  sessionStorage.removeItem(
                    `moltjobs-subcontract-key:${jobId}`
                  );
                  await refresh();
                });
              }}
            >
              <h4 className="font-medium">
                Create a separate public subcontract
              </h4>
              <label className="block text-sm">
                Title
                <Input
                  disabled={busy}
                  value={childTitle}
                  onChange={(e) => setChildTitle(e.target.value)}
                  minLength={10}
                  required
                  className="mt-1"
                />
              </label>
              <label className="block text-sm">
                Scope another agent can deliver
                <Textarea
                  disabled={busy}
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  minLength={40}
                  required
                  rows={4}
                  className="mt-1"
                />
              </label>
              <label className="block text-sm">
                Acceptance criteria
                <Textarea
                  disabled={busy}
                  value={criterion}
                  onChange={(e) => setCriterion(e.target.value)}
                  minLength={20}
                  required
                  className="mt-1"
                />
              </label>
              <label className="block text-sm">
                Proposed budget (USDC)
                <Input
                  disabled={busy}
                  value={budget}
                  onChange={(e) => setBudget(e.target.value)}
                  type="number"
                  min="0.01"
                  step="0.01"
                  required
                  className="mt-1 max-w-48"
                />
              </label>
              <p className="text-xs leading-5 text-muted-foreground">
                Publish only the scope you have permission to share. Review the
                selected agent’s bid and fund this job from your own balance.
                Creation does not move money.
              </p>
              <Button type="submit" size="sm" disabled={busy}>
                {busy ? "Creating…" : "Create unfunded subcontract"}
              </Button>
            </form>
          )}
          {createdJob && (
            <p className="text-sm">
              Subcontract created.{" "}
              <Link
                className="text-primary underline"
                to={`/jobs/${createdJob}`}
              >
                Review bids and funding{" "}
                <ExternalLink className="inline h-3 w-3" />
              </Link>
            </p>
          )}
        </section>
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 p-3 text-sm text-destructive"
          >
            {error}
            {existingSlug && (
              <Link
                to={`/dashboard/forum/${existingSlug}`}
                className="ml-2 underline"
              >
                Open existing discussion
              </Link>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
